// src/components/aols/aeo/contact/Contact.tsx
import React, { useState } from "react";
import { Button, Form, Input, Typography, message, Spin, ValidateErrorEntity } from "antd";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import { PhoneOutlined, HomeOutlined } from "@ant-design/icons";
import BackButton from "../../../dev/Styled/BackButton";
import { Footer } from "../../../dev/utils/Footer";
import styled from "styled-components";
import "./Contact.css";

// Define types for Ant Design components
import type { FormInstance } from "antd";

// Define types for form values
interface ContactFormValues {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const { Title } = Typography;

// Styled components for breadcrumbs
const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important; /* Sunlight orange for active links */
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important; /* Sunlight yellow on hover */
  }

  .breadcrumb-separator {
    color: #fff; /* White separator for contrast */
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important; /* White for non-active items */
  }
`;

const Contact: React.FC = () => {
  const [form] = Form.useForm<FormInstance<ContactFormValues>>();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  // Contact information
  const usaAddress: string = "Our Office located in the Artis School of Music at 2033 Ralston Ave., Belmont, CA, 94002";
  const egyptAddress: string = "Egypt Location in the Red Sea private office by appointments only";
  const usaPhone: string = "+13129456172";
  const egyptPhone: string = "+201550075972";

  // Structured Data for SEO (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    mainEntity: {
      "@type": "Organization",
      name: "OneLightSystem OLS",
      description: "Contact OneLightSystem OLS for inquiries about Sun Light Meditation education and support.",
      url: "https://www.olsme.com/contact",
      logo: "https://www.olsme.com/logo.jpg",
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: usaPhone,
          contactType: "Customer Service",
          areaServed: "US",
          availableLanguage: "English",
        },
        {
          "@type": "ContactPoint",
          telephone: egyptPhone,
          contactType: "Customer Service",
          areaServed: "EG",
          availableLanguage: ["English", "Arabic"],
        },
      ],
      address: [
        {
          "@type": "PostalAddress",
          streetAddress: "2033 Ralston Ave.",
          addressLocality: "Belmont",
          addressRegion: "CA",
          postalCode: "94002",
          addressCountry: "US",
        },
        {
          "@type": "PostalAddress",
          addressLocality: "Red Sea",
          addressCountry: "EG",
        },
      ],
      sameAs: [
        "https://www.facebook.com/onelightsystemols",
        "https://www.linkedin.com/company/one-light-system",
        "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
        "https://x.com/onelightsystem",
        "https://github.com/sponsors/onelightsystem",
        "https://www.blogger.com/profile/09343545978643567973",
      ],
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://www.olsme.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "OLS iee.aeo",
            item: "https://www.olsme.com/OLSaeo",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Contact",
            item: "https://www.olsme.com/contact",
          },
        ],
      },
    },
  };

  const handleSubmit = async (values: ContactFormValues) => {
    setLoading(true);
    try {
      const response = await fetch(
        import.meta.env.VITE_CONTACT_API || "https://us-central1-olsdata-550ld.cloudfunctions.net/sendMessage",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: values.name,
            email: values.email,
            subject: values.subject,
            message: values.message,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      const result = await response.json();
      console.log("Contact Form Submitted:", result);
      message.success({
        content: (
          <span>
            Thank you! Your message has been sent. We will get back to you soon.{" "}
            <Button type="link" onClick={() => navigate("/")} style={{ padding: 0 }}>
              Return to Home
            </Button>
          </span>
        ),
        duration: 5,
      });
      form.resetFields();
    } catch (error) {
      console.error("Error submitting contact form:", error);
      message.error(`Failed to send message: ${(error as Error).message || "Please try again later."}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFinishFailed = (errorInfo: ValidateErrorEntity<ContactFormValues>) => {
    console.log("Form validation failed:", errorInfo);
    message.error("Please fill in all required fields correctly.");
  };

  return (
    <div className="full-height-layout">
      <Helmet>
        <title>Contact OneLightSystem OLS | Sun Light Meditation Support</title>
        <meta
          name="description"
          content="Contact OneLightSystem OLS for inquiries about Sun Light Meditation education, support, or appointments. Reach us in Belmont, CA, or Red Sea, Egypt."
        />
        <meta
          name="keywords"
          content="OLS Contact, Sun Light Meditation, OLS Support, Light Civilization, meditation education, Belmont CA, Red Sea Egypt, contact OLS, meditation support, spiritual education"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Belmont, CA" />
        <meta name="geo.position" content="37.5202;-122.2757" />
        <meta name="ICBM" content="37.5202, -122.2757" />
        <link rel="canonical" href="https://www.olsme.com/contact" />
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content="Contact OneLightSystem OLS | Sun Light Meditation Support" />
        <meta
          property="og:description"
          content="Reach out to OneLightSystem OLS for inquiries about Sun Light Meditation education and support. Offices in Belmont, CA, and Red Sea, Egypt."
        />
        <meta property="og:url" content="https://www.olsme.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta property="og:locale" content="en_US" />
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact OneLightSystem OLS | Sun Light Meditation Support" />
        <meta
          name="twitter:description"
          content="Reach out to OneLightSystem OLS for inquiries about Sun Light Meditation education and support. Offices in Belmont, CA, and Red Sea, Egypt."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item">
            <Link to="/OLSaeo" className="breadcrumb-link">
              OLS iee.aeo
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            Contact
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>
      <div className="content-section">
        <div className="content">
          <Title level={2} className="section-title">
            Contact OneLightSystem OLS
          </Title>

          {/* Locations Section (Side by Side) */}
          <div className="locations-container">
            {/* USA Location Section */}
            <div className="contact-section">
              <h3 className="subsection-title">USA Location</h3>
              <div className="content-text">
                <p>
                  <strong>Address:</strong> {usaAddress}
                </p>
                <p>
                  <PhoneOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
                  <a href={`tel:${usaPhone}`} aria-label="Call USA office at +13129456172">
                    {usaPhone}
                  </a>
                </p>
              </div>
            </div>

            {/* Egypt Location Section */}
            <div className="contact-section">
              <h3 className="subsection-title">Egypt Location</h3>
              <div className="content-text">
                <p>
                  <strong>Address:</strong> {egyptAddress}
                </p>
                <p>
                  <PhoneOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
                  <a href={`tel:${egyptPhone}`} aria-label="Call Egypt office at +201550075972">
                    {egyptPhone}
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Google Map */}
          <div className="iframe-container">
            <iframe
              src="https://www.google.com/maps/d/u/0/embed?mid=1IeLInyV7cTtRSFS6fZj-VrbsQkA&ehbc=2E312F"
              className="google-map-iframe"
              style={{ border: 0 }}
              allowFullScreen
              title="OneLightSystem OLS Location Map"
              aria-label="Interactive map showing OneLightSystem OLS locations in Belmont, CA, and Red Sea, Egypt"
            ></iframe>
          </div>

          {/* Contact Form */}
          <Spin spinning={loading} tip="Sending your message...">
            <Form
              form={form}
              onFinish={handleSubmit}
              onFinishFailed={handleFinishFailed}
              layout="vertical"
              className="contact-form"
              aria-labelledby="contact-form-title"
            >
              <Form.Item
                label={<span className="content-text">Name</span>}
                name="name"
                rules={[{ required: true, message: "Please input your name!" }]}
              >
                <Input
                  className="custom-input"
                  placeholder="Enter your name"
                  aria-required="true"
                  aria-label="Your name"
                />
              </Form.Item>
              <Form.Item
                label={<span className="content-text">Email</span>}
                name="email"
                rules={[
                  { type: "email", message: "The input is not a valid email!" },
                  { required: true, message: "Please input your email!" },
                ]}
              >
                <Input
                  className="custom-input"
                  placeholder="Enter your email"
                  aria-required="true"
                  aria-label="Your email address"
                />
              </Form.Item>
              <Form.Item
                label={<span className="content-text">Subject</span>}
                name="subject"
                rules={[{ required: true, message: "Please input a subject!" }]}
              >
                <Input
                  className="custom-input"
                  placeholder="Enter the subject"
                  aria-required="true"
                  aria-label="Subject of your message"
                />
              </Form.Item>
              <Form.Item
                label={<span className="content-text">Message</span>}
                name="message"
                rules={[{ required: true, message: "Please input your message!" }]}
              >
                <Input.TextArea
                  rows={4}
                  className="custom-textarea"
                  placeholder="Enter your message here..."
                  aria-required="true"
                  aria-label="Your message to OneLightSystem OLS"
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={loading}
                  className="custom-button"
                  style={{ width: "100%" }}
                  aria-label="Submit contact form"
                >
                  Send Message
                </Button>
              </Form.Item>
            </Form>
          </Spin>

          {/* Social Icons (Moved to Bottom) */}
          <div className="social-follow-wrapper">
            <a href="https://github.com/sponsors/onelightsystem" target="_blank" rel="noopener noreferrer">
              <img
                src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/github.svg"
                alt="GitHub"
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  console.error("Failed to load GitHub icon");
                  e.currentTarget.src = "https://via.placeholder.com/36?text=GitHub";
                }}
              />
            </a>
            <a href="https://www.blogger.com/profile/09343545978643567973" target="_blank" rel="noopener noreferrer">
              <img
                src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/blogger.svg"
                alt="Blogger"
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  console.error("Failed to load Blogger icon");
                  e.currentTarget.src = "https://via.placeholder.com/36?text=Blogger";
                }}
              />
            </a>
            <a href="https://www.facebook.com/onelightsystemols" target="_blank" rel="noopener noreferrer">
              <img
                src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/facebook.svg"
                alt="Facebook"
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  console.error("Failed to load Facebook icon");
                  e.currentTarget.src = "https://via.placeholder.com/36?text=Facebook";
                }}
              />
            </a>
            <a href="https://www.linkedin.com/company/one-light-system" target="_blank" rel="noopener noreferrer">
              <img
                src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/linkedin.svg"
                alt="LinkedIn"
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  console.error("Failed to load LinkedIn icon");
                  e.currentTarget.src = "https://via.placeholder.com/36?text=LinkedIn";
                }}
              />
            </a>
            <a href="https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation" target="_blank" rel="noopener noreferrer">
              <img
                src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/youtube.svg"
                alt="YouTube"
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  console.error("Failed to load YouTube icon");
                  e.currentTarget.src = "https://via.placeholder.com/36?text=YouTube";
                }}
              />
            </a>
            <a href="https://x.com/onelightsystem" target="_blank" rel="noopener noreferrer">
              <img
                src="https://cdn.jsdelivr.net/npm/simple-icons@v13/icons/x.svg"
                alt="X"
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  console.error("Failed to load X icon");
                  e.currentTarget.src = "https://via.placeholder.com/36?text=X";
                }}
              />
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;